import { FirebaseAnalyticsTypes } from '@react-native-firebase/analytics';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { RootState } from 'store/index';
import { sanitizeParamValue } from 'utils/index';
import { Platform } from 'utils/platform';

import { useAnalyticsDriver } from './useAnalyticsDriver';

type ScreenViewParameters = FirebaseAnalyticsTypes.ScreenViewParameters;

const getPlatform = () => {
  if (Platform.isTV) return 'tv';
  if (Platform.OS === 'web') return 'web';
  if (['android', 'ios'].includes(Platform.OS)) return 'native';
  return 'unidentified';
};

const getPlatformSpecification = () => {
  if (Platform.isWebTV) return 'ctv';
  if (Platform.isFireTV) return 'fire_tv';
  if (Platform.isAndroidTV) return 'android_tv';
  if (Platform.isMobileWeb('ios') || Platform.OS === 'ios') return 'ios';
  if (Platform.isMobileWeb('android') || Platform.OS === 'android') return 'android';
  if (Platform.OS === 'web') return 'desktop';
  return 'unidentified';
};

const useRouteSafe = () => {
  try {
    return useRoute();
  } catch {
    return {
      name: 'unavailable',
      params: null,
    };
  }
};

export const useAnalytics = () => {
  const route = useRouteSafe();
  const analyticsDriver = useAnalyticsDriver();
  const user = useSelector((state: RootState) => state.auth);
  const profile = useSelector((state: RootState) => state.profile);

  const userId = user.bandId;
  const kidsMode = profile.isKid;
  const getClientId = analyticsDriver.getAppInstanceId as () => Promise<string>;

  const platform = getPlatform();
  const platformSpecification = getPlatformSpecification();

  const pageId = route.name;
  const pageParams = Object.entries(route.params || {})
    .map((k, v) => `${k}=${v}`)
    .join('&');

  const analyticsLogEventWithDebugger = async (name: string, params?: any) => {
    analyticsDriver.logEvent(name, params);
    const enableLog = false;

    if (__DEV__ && enableLog) {
      const obj = {
        ...params,
        date: new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
      };

      const options = {
        method: 'POST',
        body: JSON.stringify(obj),
        headers: { 'Content-Type': 'application/json' },
      };

      await fetch('http://localhost:3008/logs', options);
    }
  };

  const analyticsLogScreenView = async (screenName: string, screenClass: string) => {
    const screenViewParams: ScreenViewParameters = {
      screen_name: screenName,
      screen_class: screenClass,
    };
    analyticsDriver.logScreenView(screenViewParams);
  };

  return {
    triggerScreenView: async (screenName: string, screenClass: string) => {
      await analyticsLogScreenView(screenName, screenClass);
    },

    triggerSetUserId: async (userId: string) => {
      await analyticsDriver.setUserId(userId);
    },

    triggerSetKidsMode: async (kidsMode: string) => {
      await analyticsDriver.setUserProperty('kids_mode', kidsMode);
    },

    triggerBottomMenuViewEvent: async () => {
      await analyticsLogEventWithDebugger('pageview', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'pageview',
        action_id: 'bottombar__pageview',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'auto',
        action_reference: 'bottomBar visivel',
      });
    },

    triggerLiveScreenPageViewEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programCategory: string;
      programAdUnit: string;
      timeStamp: string;
    }) => {
      await analyticsLogEventWithDebugger('live_program_page_view', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        time_stamp: data.timeStamp,
      });
    },

    triggerLiveFinish: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programCategory: string;
      programAdUnit: string;
      timeStamp: string;
    }) => {
      await analyticsLogEventWithDebugger('live_program_finish', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        time_stamp: data.timeStamp,
      });
    },

    triggerMovieScreenPageViewEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programProducer: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('program_page_view', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_producer: data.programProducer,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerPodcastScreenPageViewEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('podcast_page_view', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerRadioScreenPageViewEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('radio_page_view', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerSeasonalScreenPageViewEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programProducer: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programSeason: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('program_page_view', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_producer: data.programProducer,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_season: data.programSeason,
        program_year: data.programYear,
      });
    },

    triggerSeasonalScreenFirstPlayEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programProducer: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programSeason: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('program_first_view', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_producer: data.programProducer,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_season: data.programSeason,
        program_year: data.programYear,
      });
    },

    triggerCategoryScreenPageViewEvent: async (data: {
      categoryId: string;
      categoryName: string;
    }) => {
      await analyticsLogEventWithDebugger('category_view', {
        user_id: userId,
        category_id: data.categoryId,
        category_name: data.categoryName,
      });
    },

    triggerTvExitConfirmationViewEvent: async () => {
      await analyticsLogEventWithDebugger('exit_confirmation_view', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'view_initial',
        action_id: 'exit_confirmation_modal_view_initial',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        platform_version: Platform.Version?.toString() || 'not mapped',
      });
    },

    //@todo validar
    triggerOnboardingScreenPageViewEvent: async () => {
      await analyticsLogEventWithDebugger('pageview', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'pageview',
        action_id: 'onboarding_screen_pageview',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'auto',
        action_reference: 'visita a pagina de onboarding',
      });
    },

    triggerSearchStartEvent: async () => {
      await analyticsLogEventWithDebugger('search_start', { user_id: userId });
    },

    triggerSearchEvent: async (searchTerm: string) => {
      await analyticsLogEventWithDebugger('search', {
        user_id: userId,
        search_term: searchTerm,
      });
    },

    triggerAccordionBandshopViewEvent: async (data: {
      isExpanded: boolean;
      collectionId: number;
      programTitle: string;
      programAgeRating: string;
      programId: string;
    }) => {
      await analyticsLogEventWithDebugger('view_inicial', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'view_inicial',
        action_id: 'accordion_bandshop_view_initial',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'auto',
        action_reference: 'view das ofertas bandshop',
        dropdown_bandshop_expanded: data.isExpanded,
        bandshop_collection_id: data.collectionId,
        program_id: data.programId,
        program_title: sanitizeParamValue(data.programTitle),
        program_age_rating: data.programAgeRating,
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerHeaderLogoActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'header_logo_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com logo no header',
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerNavigationBackActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'navigation_back_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao de voltar navegacao',
      });
    },

    triggerLoginActionEvent: async () => {
      await analyticsLogEventWithDebugger('login', { user_id: userId });
    },

    triggerCreateAccountActionEvent: async () => {
      await analyticsLogEventWithDebugger('create_account', { user_id: userId });
    },

    triggerLogoutActionEvent: async () => {
      await analyticsLogEventWithDebugger('logout', { user_id: userId });
    },

    triggerKeepKidsModeActionEvent: async () => {
      await analyticsLogEventWithDebugger('keep_kids_mode', { user_id: userId });
    },

    triggerLoginAgainActionEvent: async () => {
      await analyticsLogEventWithDebugger('login_again', { user_id: userId });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerSeeMoreActionEvent: async (data: { shelfTitle: string; navigationTarget: string }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'see_more_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'interacao com botao de ver mais nas prateleiras',
        shelf_title: data.shelfTitle,
        navigation_target: data.navigationTarget,
      });
    },

    triggerMediaEnableVideoActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('radio_video_enable', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerPlayerEvent: async (data: {
      user_id?: string;
      mediaType: 'audio' | 'video';
      programTitle: string;
      programAgeRating: string;
      programId: string;
      programSeason?: string;
      programEpIndex?: string;
      programEpTitle?: string;
      programProducer?: string;
      ClickWatchTrailer?: boolean;
      ClickToExternalLink?: string;
      playerLocation?: 'home' | 'program';
    }) => {
      await analyticsLogEventWithDebugger('view_inicial', {
        user_id: userId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_id: data.programId,
        program_producer: data.programProducer,
        program_season: data.programSeason,
        program_ep_index: data.programEpIndex,
        program_ep_title: data.programEpTitle,
        button_assistirEP_max: data.ClickToExternalLink,
        button_assistirTrailer_max: data.ClickWatchTrailer,
        player_screen_name: data.playerLocation,
      });
    },

    triggerEventExternalLink: async (data: {
      mediaType: 'audio' | 'video';
      programTitle: string;
      programAgeRating: string;
      programId: string;
      programSeason?: string;
      programEpTitle?: string;
      ClickWatchTrailer?: boolean;
      ClickToExternalLink?: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        user_id: userId,
        program_title: data.programTitle,
        action_type: 'action',
        action_id: 'cta_newco_click',
        action_trigger_type: 'link_button',
        action_reference:
          'Clique no botão de “ver mais” para abrir uma aba no navegador com o endereço da plataforma Newco Play',
        program_age_rating: data.programAgeRating,
        program_id: data.programId,
        program_season: data.programSeason,
        program_ep_title: data.programEpTitle,
        button_external_partner_link: data.ClickToExternalLink,
        button_assistir_partner_trailer: data.ClickWatchTrailer,
      });
    },

    triggerMediaDisableVideoActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('radio_video_disable', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerMediaPlayActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programProducer: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      if (data.programTitle.includes('Ao Vivo')) {
        await analyticsLogEventWithDebugger('live_program_start', {
          user_id: userId,
          program_id: data.programId,
          program_title: data.programTitle,
          program_age_rating: data.programAgeRating,
          program_genre: data.programGenre,
          program_producer: data.programProducer,
          program_director: data.programDirector,
          program_category: data.programCategory,
          program_ad_unit: data.programAdUnit,
          program_year: data.programYear,
        });
      } else {
        await analyticsLogEventWithDebugger('program_start', {
          user_id: userId,
          program_id: data.programId,
          program_title: data.programTitle,
          program_age_rating: data.programAgeRating,
          program_genre: data.programGenre,
          program_producer: data.programProducer,
          program_director: data.programDirector,
          program_category: data.programCategory,
          program_ad_unit: data.programAdUnit,
          program_year: data.programYear,
        });
      }
    },

    triggerMediaPauseActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programProducer: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      if (data.programTitle.includes('Ao Vivo')) {
        await analyticsLogEventWithDebugger('live_program_pause', {
          user_id: userId,
          program_id: data.programId,
          program_title: data.programTitle,
          program_age_rating: data.programAgeRating,
          program_genre: data.programGenre,
          program_producer: data.programProducer,
          program_director: data.programDirector,
          program_category: data.programCategory,
          program_ad_unit: data.programAdUnit,
          program_year: data.programYear,
        });
      } else {
        await analyticsLogEventWithDebugger('program_pause', {
          user_id: userId,
          program_id: data.programId,
          program_title: data.programTitle,
          program_age_rating: data.programAgeRating,
          program_genre: data.programGenre,
          program_director: data.programDirector,
          program_category: data.programCategory,
          program_ad_unit: data.programAdUnit,
          program_year: data.programYear,
        });
      }
    },

    triggerMediaPreviousActionEvent: async (data: {
      mediaType: 'audio' | 'video';
      programTitle: string;
      programAgeRating: string;
      programId: string;
      programSeason?: string;
      programEpTitle?: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'media_previous_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao "previous" de um player',
        media_type: data.mediaType,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_id: data.programId,
        program_season: data.programSeason,
        program_ep_title: data.programEpTitle,
      });
    },

    triggerMediaNextActionEvent: async (data: {
      mediaType: 'audio' | 'video';
      programTitle: string;
      programAgeRating: string;
      programId: string;
      programSeason?: string;
      programEpTitle?: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'media_next_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao "next" de um player',
        media_type: data.mediaType,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_id: data.programId,
        program_season: data.programSeason,
        program_ep_title: data.programEpTitle,
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerMediaFullscreenActionEvent: async (data: {
      mediaType: 'audio' | 'video';
      playerCurrentStatus: 'fullscreen' | 'windowed';
      playerDesiredStatus: 'fullscreen' | 'windowed';
      programTitle: string;
      programAgeRating: string;
      programId: string;
      programSeason?: string;
      programEpTitle?: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'media_fullscreen_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao "fullscreen" de um player',
        media_type: data.mediaType,
        player_current_status: data.playerCurrentStatus,
        player_desired_status: data.playerDesiredStatus,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_id: data.programId,
        program_season: data.programSeason,
        program_ep_title: data.programEpTitle,
      });
    },

    // @TODO: precisa adicionar parâmetros de programa
    triggerMediaWatchedActionEvent: async (data: {
      mediaDuration: number;
      mediaProgress: number;
    }) => {
      await analyticsLogEventWithDebugger('program_progress', {
        user_id: userId,
        media_duration: data.mediaDuration,
        media_progress: data.mediaProgress,
      });
    },

    triggerMediaAutonextCancelActionEvent: async (data: { programTitle: string }) => {
      await analyticsLogEventWithDebugger('autonext_cancel', {
        user_id: userId,
        program_title: data.programTitle,
      });
    },

    triggerMediaAutonextImmediateActionEvent: async (data: { programTitle: string }) => {
      await analyticsLogEventWithDebugger('autonext_forced', {
        user_id: userId,
        program_title: data.programTitle,
      });
    },

    triggerMediaAutonextTimerActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('autonext', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerCarouselNextActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'carousel_next_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao de ir para o proximo slide de um carrossel',
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerCarouselPreviousActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'carousel_previous_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao de ir para o slide anterior de um carrossel',
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerUseChromecastActionEvent: async (data: {
      triggerLocation: 'menu' | 'player';
      programTitle?: string;
      programAgeRating?: string;
      programId?: string;
      programSeason?: string;
      programEpTitle?: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'use_chromecast_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao de sincronizar com chromecast',
        trigger_location: data.triggerLocation,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_id: data.programId,
        program_season: data.programSeason,
        program_ep_title: data.programEpTitle,
      });
    },

    // @TODO: precisa ser consumido nos componentes
    triggerCardClickActionEvent: async (data: {
      cardNavigationTarget: string;
      cardImage: string;
      cardTitle: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'card_click_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'card',
        action_reference: 'interacao com um card especifico',
        card_navigation_target: data.cardNavigationTarget,
        card_image: data.cardImage,
        card_title: data.cardTitle,
      });
    },

    triggerReviewPageviewEvent: async () => {
      await analyticsLogEventWithDebugger('pageview', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'pageview',
        action_id: 'review_pageview',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'auto',
        action_reference: 'visualizacao do modal de review',
      });
    },

    triggerReviewLikeActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'review_like_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'interacao com botao de like no modal de review',
      });
    },

    triggerReviewDislikeActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'review_dislike_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'interacao com botao de dislike no modal de review',
      });
    },

    triggerChangeToKidsModeActionEvent: async () => {
      await analyticsLogEventWithDebugger('kids_mode_activate', { user_id: userId });
    },

    triggerExitFromKidsModeActionEvent: async () => {
      await analyticsLogEventWithDebugger('kids_mode_deactivate', { user_id: userId });
    },

    triggerGoToMyProfileActionEvent: async () => {
      await analyticsLogEventWithDebugger('menu_meu_perfil', { user_id: userId });
    },

    triggerGoToFavoritesActionEvent: async () => {
      await analyticsLogEventWithDebugger('menu_minha_lista', { user_id: userId });
    },

    triggerGoToAgeRatingConfigurationActionEvent: async () => {
      await analyticsLogEventWithDebugger('age_rating_setting', { user_id: userId });
    },

    triggerGoToFaqActionEvent: async () => {
      await analyticsLogEventWithDebugger('menu_ajuda', { user_id: userId });
    },

    triggerGoToLoginActionEvent: async () => {
      await analyticsLogEventWithDebugger('login_menu', { user_id: userId });
    },

    triggerLiveMenuItemActionEvent: async (data: {
      liveMenuItemName: string;
      liveMenuItemUrl?: string;
    }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'live_menu_item_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'interacao com item do live menu',
        live_menu_item_name: data.liveMenuItemName,
        live_menu_item_url: data.liveMenuItemUrl,
      });
    },

    triggerOnEnterPictureInPictureActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'on_enter_picture_in_picture_action_event',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'auto',
        action_reference: 'evento disparado quando pip é ativado',
      });
    },

    triggerOnExitPictureInPictureActionEvent: async () => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'on_exit_picture_in_picture_action_event',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'auto',
        action_reference: 'evento disparado quando pip é desativado',
      });
    },

    triggerAddToFavoritesActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('add_to_favorites', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerSeekEventOnPlayer: async (data: {
      programId: string;
      programTitle: string;
      source: string;
      direction: string;
    }) => {
      await analyticsLogEventWithDebugger('player_seek', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        action_id: 'seek',
        ation_trigger_type: data.source,
        direction: data.direction,
      });
    },

    triggerRemoveToFavoritesActionEvent: async (data: {
      programId: string;
      programTitle: string;
      programAgeRating: string;
      programGenre: string;
      programDirector: string;
      programCategory: string;
      programAdUnit: string;
      programYear: string;
    }) => {
      await analyticsLogEventWithDebugger('remove_from_favorites', {
        user_id: userId,
        program_id: data.programId,
        program_title: data.programTitle,
        program_age_rating: data.programAgeRating,
        program_genre: data.programGenre,
        program_director: data.programDirector,
        program_category: data.programCategory,
        program_ad_unit: data.programAdUnit,
        program_year: data.programYear,
      });
    },

    triggerShelfScrollActionEvent: async (data: { scrollSide: 'left' | 'right' }) => {
      await analyticsLogEventWithDebugger('action', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        action_id: 'shelf_scroll_action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'icon_button',
        action_reference: 'interacao com botao de scrolar prateleira',
        scroll_side: data.scrollSide,
      });
    },

    triggerAccordionBandshopActionEvent: async (data: {
      isExpanded: boolean;
      programTitle: string;
      programAgeRating: string;
      programId: string;
    }) => {
      await analyticsLogEventWithDebugger('accordion_bandshop_click', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'accordion',
        action_reference: 'clique em ofertas bandshop para expandir ou fechar o accordion',
        dropdown_bandshop_expanded: data.isExpanded,
        program_id: data.programId,
        program_title: sanitizeParamValue(data.programTitle),
        program_age_rating: data.programAgeRating,
      });
    },

    triggerBandshopProductButtonActionEvent: async (data: {
      productTitle: string;
      programTitle: string;
      programAgeRating: string;
      programId: string;
    }) => {
      await analyticsLogEventWithDebugger('bandshop_button_ver_detalhes_click', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'clique no botao ver detalhes do card de um produto bandshop',
        card_carrossel_bandshop: sanitizeParamValue(data.productTitle),
        program_id: data.programId,
        program_title: sanitizeParamValue(data.programTitle),
        program_age_rating: data.programAgeRating,
      });
    },

    triggerCarouselBandshopArrowScrollActionEvent: async (data: {
      scrollSide: 'left' | 'right';
      programTitle: string;
      programAgeRating: string;
      programId: string;
    }) => {
      await analyticsLogEventWithDebugger('carousel_bandshop_arrow_scroll', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'carousel',
        action_reference: 'rolagem lateral do carousel de produtos do bandshop',
        program_id: data.programId,
        program_title: sanitizeParamValue(data.programTitle),
        program_age_rating: data.programAgeRating,
        scroll_side: data.scrollSide,
      });
    },

    triggerCarouselBandshopMobileScrollActionEvent: async (data: {
      programTitle: string;
      programAgeRating: string;
      programId: string;
    }) => {
      await analyticsLogEventWithDebugger('carousel_bandshop_scroll', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        page_id: pageId,
        page_params: pageParams,
        kids_mode: kidsMode,
        action_trigger_type: 'carousel',
        action_reference: 'rolagem lateral do carousel de produtos do bandshop',
        program_id: data.programId,
        program_title: sanitizeParamValue(data.programTitle),
        program_age_rating: data.programAgeRating,
      });
    },

    triggerTvExitConfirmationExitActionEvent: async () => {
      await analyticsLogEventWithDebugger('button_exit_confirmation_modal_sair_click', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'Clique no cta ”Sair do Bandplay” para sair o aplicativo na televisao',
        platform_version: Platform.Version?.toString() || 'not mapped',
      });
    },

    triggerTvExitConfirmationCancelActionEvent: async () => {
      await analyticsLogEventWithDebugger('button_exit_confirmation_modal_voltar_click', {
        client_id: await getClientId(),
        user_id: userId,
        platform: platform,
        platform_specification: platformSpecification,
        action_type: 'action',
        kids_mode: kidsMode,
        action_trigger_type: 'labeled_button',
        action_reference: 'Clique no cta ”Voltar para a navegação” para retornar para navegação',
        platform_version: Platform.Version?.toString() || 'not mapped',
      });
    },

    triggerViewProgramListEvent: async (data: {
      programTitle: string;
      programListName: string;
    }) => {
      await analyticsLogEventWithDebugger('view_program_list', {
        user_id: userId,
        program_title: data.programTitle,
        program_list_name: data.programListName,
      });
    },

    triggerSelectProgramEvent: async (data: { programTitle: string; programListName: string }) => {
      await analyticsLogEventWithDebugger('select_program', {
        user_id: userId,
        program_title: data.programTitle,
        program_list_name: data.programListName,
      });
    },

    triggerViewPromotionEvent: async (data: {
      promotionId: string;
      promotionName: string;
      creativeName: string;
      creativeSlot: string;
    }) => {
      await analyticsLogEventWithDebugger('view_promotion', {
        user_id: userId,
        promotion_id: data.promotionId,
        promotion_name: data.promotionName,
        creative_name: data.creativeName,
        creative_slot: data.creativeSlot,
      });
    },

    triggerSelectPromotionEvent: async (data: {
      promotionId: string;
      promotionName: string;
      creativeName: string;
      creativeSlot: string;
    }) => {
      await analyticsLogEventWithDebugger('select_promotion', {
        user_id: userId,
        promotion_id: data.promotionId,
        promotion_name: data.promotionName,
        creative_name: data.creativeName,
        creative_slot: data.creativeSlot,
      });
    },
  };
};
